import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import classNames from 'classnames';
import css from './UnderRow.css';
import Icon, { IconTypes } from '../../../../../components/foundations/Icon';
import Button from '../../../../../components/atoms/Button';
import { STATUSES, TTypeEnum } from '../consts';
import UnderRowContent from './UnderRowContent';
import { getUserData } from '../../../../../selectors/user';
import AccountPlanAPI from '../../../../../organizationPlan/service';

const iconNameMap = {
    [TTypeEnum.ALERT]: 'alert-triangle',
    [TTypeEnum.ERROR]: 'circle-x',
    [TTypeEnum.INFO]: 'info-circle',
};

const typeClassMap = {
    [TTypeEnum.ALERT]: css.underRowAlert,
    [TTypeEnum.ERROR]: css.underRowError,
    [TTypeEnum.INFO]: css.underRowInfo,
};
const TRANSLATION_PREFIX = 'STATIC.PAGES.MANAGE_LINKS.SUB_DOMAIN.TABLE.UNDER_ROW';
const accountPlanAPI = new AccountPlanAPI();

const UnderRow = ({
    type = TTypeEnum.INFO,
    className = '',
    message = '',
    status = null,
    actionButtonText = '',
    domain = '',
    showSDKInstructions = false,
}) => {
    const { company: orgName = '', organization_tier: orgTier } = useSelector(getUserData);
    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const onButtonClick = async () => {
        setIsLoadingButton(true);
        const { success } = await accountPlanAPI.submitContactUs(orgTier);
        if (success) {
            setShowSuccess(true);
        }
        setIsLoadingButton(false);
    };

    const getIconName = () => {
        return iconNameMap[type] || TTypeEnum.INFO;
    };

    const getTypeClass = () => {
        return typeClassMap[type] || css.underRowInfo;
    };

    const renderMessage = () => {
        if (status === STATUSES.PENDING_CNAME) {
            return (
                <div className={css.specialMessage}>
                    <div className={css.messageTitle}>
                        <Translate id={`${TRANSLATION_PREFIX}.TROUBLESHOOTING.TITLE`} />
                    </div>
                    <ol className={css.troubleshootingList}>
                        <li>
                            <Translate id={`${TRANSLATION_PREFIX}.TROUBLESHOOTING.VERIFY_CNAME`} />
                        </li>
                        <li>
                            <Translate id={`${TRANSLATION_PREFIX}.TROUBLESHOOTING.CONTACT_IT`} />
                        </li>
                    </ol>
                </div>
            );
        }

        if (status === STATUSES.PENDING_CERTIFICATE) {
            return (
                <div className={css.specialMessage}>
                    <div className={css.messageTitle}>
                        <Translate id={`${TRANSLATION_PREFIX}.CERTIFICATE.CREATING_TITLE`} />
                    </div>
                    <div className={css.messageDescription}>
                        <Translate id={`${TRANSLATION_PREFIX}.CERTIFICATE.CREATING_DESCRIPTION`} />
                    </div>
                </div>
            );
        }

        if (message) {
            return (
                <span className={css.underRowMessage}>
                    <Translate id={`${TRANSLATION_PREFIX}.${message}`} />
                </span>
            );
        }

        return null;
    };

    const shouldShowAlert = message && (type === 'alert' || type === 'error' || type === 'info');

    return (
        <div className={classNames(css.underRowContainer, getTypeClass(), className)}>
            {shouldShowAlert && (
                <div className={css.underRowContent}>
                    <div className={classNames(css.underRowIconAndMessage, css.underRowIconAndMessageType)}>
                        <span className={css.underRowIcon}>
                            <Icon name={getIconName()} type={IconTypes.TABLER} className={css.icon} />
                        </span>
                        <div className={css.underRowMessageContainer}>{renderMessage()}</div>
                        {actionButtonText && (
                            <Button
                                showV={showSuccess}
                                type="secondary"
                                showSpinner={isLoadingButton}
                                onClick={onButtonClick}
                            >
                                <Translate id={actionButtonText} />
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {domain && <UnderRowContent orgName={orgName} domain={domain} showSDKInstructions={showSDKInstructions} />}
        </div>
    );
};

UnderRow.propTypes = {
    type: PropTypes.oneOf(['alert', 'error', 'info']),
    message: PropTypes.string,
    actionButton: PropTypes.node,
    className: PropTypes.string,
    actionButtonText: PropTypes.string,
    orgName: PropTypes.string,
    domain: PropTypes.string,
    status: PropTypes.oneOf([
        STATUSES.PENDING_CERTIFICATE,
        STATUSES.EDIT,
        STATUSES.PENDING_CNAME,
        STATUSES.UPGRADE,
        STATUSES.LIMIT_REACHED,
    ]),
    showSDKInstructions: PropTypes.bool,
};

export default UnderRow;
